// Phase 85.A.69 — Modal hướng dẫn chi tiết khi OCR rating fail lần đầu
// trong session (user choice Q6=b: session-local flag, refresh page reset).
//
// Phase 85.A.85 — RÚT GỌN: Phase 85.A.83 wizard upload đã có ảnh hướng dẫn
// chi tiết 3 SAI + 1 ĐÚNG trước khi user upload. Modal này hiển thị SAU
// khi OCR thất bại, không cần lặp lại 2 bước đầu (mở app, chụp screenshot).
// Chỉ giữ checklist quan trọng nhất: ảnh phải có TÊN + ĐIỂM TRÌNH.
//
// Phase 85.A.97 — BẮT BUỘC user bấm "Tôi đã hiểu — Thử lại" để đóng. Bỏ nút
// X + bỏ click-outside-to-close để chống user dismiss hời hợt rồi spam upload
// đúng ảnh sai.
import { m as motion, AnimatePresence } from "framer-motion";
import {
  IdentificationCard,
  CheckCircle,
  ArrowRight,
} from "@phosphor-icons/react";

import RatingVerifyIllustration from "./RatingVerifyIllustration";

export default function OCRFailureGuideModal({ open, onClose, onRetry, errorDetail }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[400] flex items-end sm:items-center justify-center bg-black/75 backdrop-blur-md px-3 pb-4 sm:pb-0"
          data-testid="ocr-failure-guide-modal"
        >
          <motion.div
            initial={{ y: 32, scale: 0.96, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 32, scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="w-full max-w-xl rounded-3xl border border-amber-400/30 bg-gradient-to-br from-[#1A0F1C] via-[#0F1623] to-[#0A0F1C] shadow-[0_24px_80px_rgba(255,138,0,0.4)] overflow-hidden flex flex-col max-h-[92dvh]"
          >
            {/* Header — Phase 85.A.97: bỏ nút X để user BUỘC đọc xong rồi bấm CTA. */}
            <div className="px-5 py-4 flex items-start gap-3 border-b border-white/8 bg-gradient-to-r from-rose-500/8 via-amber-500/8 to-transparent">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-rose-500/30 to-amber-500/20 border border-rose-400/40 flex items-center justify-center shrink-0">
                <IdentificationCard size={20} weight="fill" className="text-amber-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-amber-300/80">
                  Lỗi OCR
                </div>
                <div className="text-[16px] font-black text-white leading-tight mt-0.5">
                  Ảnh chưa đủ điều kiện đọc tự động
                </div>
                <div className="text-[11.5px] text-white/55 mt-1 leading-snug">
                  Vui lòng kiểm tra checklist dưới đây, chụp lại và thử lại.
                </div>
              </div>
            </div>

            {/* Content — chỉ giữ bước 3: phải có tên + điểm trình */}
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
              <div
                className="flex items-start gap-3 p-4 rounded-2xl border"
                style={{
                  background: "linear-gradient(135deg, #FF8A0012, #FF8A0004)",
                  borderColor: "#FF8A0040",
                }}
              >
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 border border-white/10"
                  style={{ background: "linear-gradient(135deg, #FF8A0028, #FF8A0010)" }}
                >
                  <IdentificationCard size={20} weight="fill" style={{ color: "#FF8A00" }} />
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                  <div className="text-[14.5px] font-black text-white leading-tight">
                    Phải có tên + điểm trình
                  </div>
                  <div className="text-[12.5px] text-white/75 leading-relaxed mt-1.5">
                    Đảm bảo ảnh chứa rõ ràng 2 thông tin: <strong className="text-white">HỌ TÊN</strong> khớp với tài khoản PickLeague + <strong className="text-white">ĐIỂM TRÌNH</strong> (đôi/đơn). Nếu ảnh thiếu 1 trong 2 → OCR sẽ báo lỗi.
                  </div>
                </div>
              </div>

              <div data-testid="ocr-failure-guide-illustration">
                <RatingVerifyIllustration
                  maxHeight={260}
                  testId="ocr-failure-guide-mascot"
                />
                <ul className="mt-3 space-y-1.5">
                  {[
                    "Ảnh có ĐẦY ĐỦ họ tên (số 1️⃣)",
                    "Ảnh có ĐIỂM TRÌNH rõ ràng (số 2️⃣)",
                    "Ảnh không bị mờ, không cắt thiếu thông tin",
                  ].map((txt, i) => (
                    <li key={i} className="flex items-start gap-2 text-[11.5px] text-white/80 leading-snug">
                      <CheckCircle size={13} weight="fill" className="text-emerald-300 shrink-0 mt-0.5" />
                      <span>{txt}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {errorDetail && (
                <div className="rounded-xl border border-rose-400/30 bg-rose-500/8 p-3 text-[11px] text-rose-200/85 leading-snug" data-testid="ocr-failure-guide-error-detail">
                  <strong className="text-rose-200">Lỗi cụ thể từ hệ thống:</strong>{" "}
                  {errorDetail}
                </div>
              )}
            </div>

            {/* Footer — single CTA */}
            <div className="px-5 py-4 border-t border-white/8 bg-black/30 flex items-center justify-end">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onRetry?.();
                }}
                className="h-10 px-4 rounded-xl bg-gradient-to-r from-[#FF8A00] to-[#FF9F1C] text-white text-[12.5px] font-black transition flex items-center gap-1.5 shadow-[0_6px_18px_rgba(255,138,0,0.4)]"
                data-testid="ocr-failure-guide-retry"
              >
                Tôi đã hiểu — Thử lại
                <ArrowRight size={13} weight="bold" />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
